/**
 * WardrobeAddProgressModal — Progression visuelle de l'ajout d'un article
 *
 * Affiche un popup branded MyTenue qui suit l'ajout d'un article :
 *   1. uploading   : upload de la photo sur S3
 *   2. analyzing   : analyse IA Claude Vision en cours
 *   3. done        : article ajouté avec succès → auto-close après 1.5s
 *   4. error       : un message d'erreur + bouton Fermer
 *
 * Usage :
 *   <WardrobeAddProgressModal
 *     state={addProgress}             // null | 'uploading' | 'analyzing' | 'done' | 'error'
 *     errorMessage={...}              // affiché si state === 'error'
 *     onClose={() => setAddProgress(null)}
 *   />
 */

import React, { useEffect, useRef } from 'react';
import { View, Text, ActivityIndicator, StyleSheet, Animated, Easing, TouchableOpacity } from 'react-native';
import AppModal from './AppModal';
import { useI18n } from '../i18n';

// v1.12.329 — i18n FR/EN des étapes
function getSteps(en) {
  return [
    { key: 'uploading', icon: '⬆',  label: en ? 'Photo upload'      : 'Upload de la photo' },
    { key: 'analyzing', icon: '✨', label: en ? 'AI analysis…'      : 'Analyse IA en cours' },
    { key: 'done',      icon: '✓',  label: en ? 'Item added!'       : 'Article ajouté !'   },
  ];
}

export default function WardrobeAddProgressModal({ state, errorMessage, onClose }) {
  const { lang } = useI18n();
  const en = lang === 'en';
  const STEPS = React.useMemo(() => getSteps(en), [en]);
  // Animation de la barre de progression (indeterminée — boucle gauche-droite)
  const progressAnim = useRef(new Animated.Value(0)).current;
  const isActive = state === 'uploading' || state === 'analyzing';

  useEffect(() => {
    if (!isActive) {
      progressAnim.setValue(0);
      return undefined;
    }
    const loop = Animated.loop(
      Animated.sequence([
        Animated.timing(progressAnim, { toValue: 1, duration: 1400, easing: Easing.inOut(Easing.ease), useNativeDriver: false }),
        Animated.timing(progressAnim, { toValue: 0, duration: 0,    useNativeDriver: false }),
      ]),
    );
    loop.start();
    return () => loop.stop();
  }, [isActive, progressAnim]);

  // v1.13.82 — onClose via REF : le parent passe une arrow INLINE (nouvelle réf à
  //   chaque render). Si l'effet dépendait de onClose, chaque re-render du parent
  //   (loadWardrobe, etc. juste après l'ajout) relançait l'effet → son cleanup
  //   clearTimeout puis re-setTimeout → le timer 1.5s n'atteignait JAMAIS son
  //   terme → le popup "Article ajouté !" restait affiché très longtemps.
  const onCloseRef = useRef(onClose);
  useEffect(() => { onCloseRef.current = onClose; }, [onClose]);

  // Auto-close après 'done' (1.5 sec) — dépend UNIQUEMENT de state
  useEffect(() => {
    if (state !== 'done') return undefined;
    const t = setTimeout(() => { onCloseRef.current && onCloseRef.current(); }, 1500);
    return () => clearTimeout(t);
  }, [state]);

  if (!state) return null;

  // Détermine quel step est actif (et lesquels sont complétés)
  const activeIdx = state === 'uploading' ? 0 : (state === 'analyzing' ? 1 : 2);
  const isDone = state === 'done';
  const isError = state === 'error';

  return (
    <AppModal visible transparent animationType="fade" onRequestClose={isError ? onClose : undefined} webBackdropClosable={false}>
      <View style={s.overlay}>
        <View style={s.card}>
          {/* Header */}
          <View style={s.headerRow}>
            <View style={[s.iconCircle, isError && s.iconCircleError, isDone && s.iconCircleDone]}>
              <Text style={s.iconText}>
                {isError ? '⚠' : isDone ? '✓' : '✨'}
              </Text>
            </View>
            <View style={{ flex: 1 }}>
              <Text style={s.title}>
                {isError
                  ? (en ? 'Error' : 'Erreur')
                  : isDone
                    ? (en ? 'Item added!' : 'Article ajouté !')
                    : (en ? 'Adding in progress' : 'Ajout en cours')}
              </Text>
              <Text style={s.subtitle}>
                {isError
                  ? (en ? "Couldn't finish adding the item." : "L'ajout n'a pas pu aboutir.")
                  : isDone
                    ? (en ? 'Your item is now in your wardrobe.' : 'Votre article est dans votre dressing.')
                    : (en ? 'This can take a few seconds…' : 'Cela peut prendre quelques secondes…')}
              </Text>
            </View>
          </View>

          {/* Étapes */}
          {!isError && (
            <View style={s.steps}>
              {STEPS.map((step, idx) => {
                const completed = idx < activeIdx || (isDone && idx <= activeIdx);
                const active    = idx === activeIdx && !isDone;
                return (
                  <View key={step.key} style={s.stepRow}>
                    <View style={[
                      s.stepBullet,
                      completed && s.stepBulletDone,
                      active && s.stepBulletActive,
                    ]}>
                      {completed ? (
                        <Text style={s.stepBulletText}>✓</Text>
                      ) : active ? (
                        <ActivityIndicator size="small" color="#FFFFFF" />
                      ) : (
                        <Text style={[s.stepBulletText, s.stepBulletTextIdle]}>{idx + 1}</Text>
                      )}
                    </View>
                    <Text style={[
                      s.stepLabel,
                      (completed || active) && s.stepLabelActive,
                    ]}>
                      {step.label}
                    </Text>
                  </View>
                );
              })}
            </View>
          )}

          {/* Barre de progression indéterminée */}
          {isActive && (
            <View style={s.progressTrack}>
              <Animated.View style={[
                s.progressFill,
                {
                  left: progressAnim.interpolate({ inputRange: [0, 1], outputRange: ['-30%', '100%'] }),
                },
              ]} />
            </View>
          )}

          {/* Message erreur */}
          {isError && (
            <View style={s.errorBox}>
              <Text style={s.errorText}>{errorMessage || (en ? 'An error occurred.' : 'Une erreur est survenue.')}</Text>
              <Text style={s.supportText}>
                {en ? 'If the problem persists, contact us at' : 'Si le problème persiste, contactez-nous à'}{'\n'}
                <Text style={s.supportLink}>support@mytenue.com</Text>
              </Text>
              <TouchableOpacity style={s.closeBtn} onPress={onClose} activeOpacity={0.85}>
                <Text style={s.closeBtnText}>{en ? 'CLOSE' : 'FERMER'}</Text>
              </TouchableOpacity>
            </View>
          )}
        </View>
      </View>
    </AppModal>
  );
}

const s = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'transparent',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 16,
  },
  card: {
    width: '100%',
    maxWidth: 400,
    backgroundColor: '#FFFFFF',
    borderRadius: 18,
    // v1.13.61 — bordure dorée sur le popup de progression d'ajout
    borderWidth: 1.5,
    borderColor: '#D4AF72',
    padding: 22,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.18,
    shadowRadius: 24,
    elevation: 8,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
    marginBottom: 18,
  },
  iconCircle: {
    width: 52, height: 52, borderRadius: 26,
    backgroundColor: '#9B7B5A',
    alignItems: 'center', justifyContent: 'center',
  },
  iconCircleDone:  { backgroundColor: '#22C55E' },
  iconCircleError: { backgroundColor: '#DC2626' },
  iconText: { fontSize: 22, color: '#FFFFFF', fontWeight: '700' },
  title:    { fontSize: 16, fontWeight: '700', color: '#1A1814', letterSpacing: 0.2 },
  subtitle: { fontSize: 12, color: '#7A7268', marginTop: 2 },

  steps: { gap: 12, marginBottom: 16 },
  stepRow: { flexDirection: 'row', alignItems: 'center', gap: 12 },
  stepBullet: {
    width: 24, height: 24, borderRadius: 12,
    backgroundColor: '#E8E4DC',
    alignItems: 'center', justifyContent: 'center',
  },
  stepBulletActive: { backgroundColor: '#9B7B5A' },
  stepBulletDone:   { backgroundColor: '#22C55E' },
  stepBulletText:   { fontSize: 11, fontWeight: '700', color: '#FFFFFF' },
  stepBulletTextIdle: { color: '#A89E94' },
  stepLabel:        { fontSize: 13, color: '#A89E94', flex: 1 },
  stepLabelActive:  { color: '#1A1814', fontWeight: '500' },

  progressTrack: {
    height: 3, backgroundColor: '#F0EDE6',
    borderRadius: 2, overflow: 'hidden', marginTop: 6,
    position: 'relative',
  },
  progressFill: {
    position: 'absolute',
    top: 0, bottom: 0,
    width: '30%',
    backgroundColor: '#9B7B5A',
    borderRadius: 2,
  },

  errorBox: { gap: 12 },
  errorText: { fontSize: 13, color: '#7A7268', lineHeight: 18 },
  supportText: { fontSize: 11, color: '#9A8E7E', lineHeight: 15, textAlign: 'center' },
  supportLink: { color: '#9B7B5A', fontWeight: '600' },
  closeBtn: {
    backgroundColor: '#1A1814',
    borderRadius: 100, paddingVertical: 12,
    alignItems: 'center', marginTop: 4,
  },
  closeBtnText: { color: '#FFFFFF', fontSize: 12, fontWeight: '700', letterSpacing: 1.5 },
});
